import {Component, OnDestroy, OnInit} from '@angular/core';
import {RouterLink} from '@angular/router';
import {DecimalPipe, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatTooltipModule} from '@angular/material/tooltip';
import {combineLatest, Subscription} from 'rxjs';

import {
  CriterionDef,
  EvaluationAudioClip,
  EvaluationGame,
  EvaluationSeason,
  EvidenceEvent,
} from '../../model/evaluation/evaluation';
import {isEvaluationAdmin, User} from '../../model/user';
import {AuthService} from '../../services/auth.service';
import {EvaluationsService} from '../../services/evaluations.service';
import {computeReward, failedGates, maximumCut, pendingCriteria} from '../reward';
import {EvidenceAudioComponent} from '../evidence-audio.component';
import {EvidenceImageComponent} from '../evidence-image.component';
import {RecordingPlayerComponent} from '../recording-player.component';

interface WorkItem {
  key: string;
  game: EvaluationGame;
  def: CriterionDef;
  note: string;
  at: number | null;
  evidence: EvidenceEvent[];
}

/**
 * Criteria settled by listening rather than by looking. The tool publishes a
 * minute of the broadcast's sound taken from the lineup presentation, and for
 * these two that minute *is* the evidence — sending the reviewer to the detail
 * screen to play it would be a click for the one thing the queue is for.
 *
 * The commentary is the obvious one: the graphic is up, the game is paused,
 * and whoever commentates says the names. Ambient sound rides along because
 * the criterion only ever looks for a dead track — a minute with any sound at
 * all on it answers that, wherever in the broadcast it came from.
 */
const AUDIO_FOR = ['liveCommentary', 'ambientSound'];

/** Stills worth showing next to a given criterion. */
const EVIDENCE_FOR: { [criterionId: string]: (event: EvidenceEvent) => boolean } = {
  cameras: event => event.kind === 'marker',
  scoreboard: event => event.kind.startsWith('scoreboard'),
  continuity: event => event.kind.startsWith('outage'),
  opener: event => event.slot === 'opener',
  sponsor01: event => event.slot === 'SV 01b',
  sponsor03: event => event.slot === 'SV 03b',
  sponsor05: event => event.slot === 'SV 05b',
  sponsor07: event => event.slot === 'SV 07b',
  sponsor08: event => event.slot === 'SV 08b',
  lineupsT1: event => event.kind.startsWith('badge:'),
  lineupsM1: event => event.kind.startsWith('badge:'),
  direction: event => event.kind === 'marker',
};

/**
 * The queue of everything a human still has to decide, one item at a time.
 *
 * Last season this was 98 rulings across 50 of 198 broadcasts, and they cluster
 * by production company — Hroši 18, Arrows 17, SaBaT 2. The queue is therefore
 * ordered by producer: an admin settles one production in a sitting instead of
 * opening fifty games.
 */
@Component({
  selector: 'app-evaluation-review-wizard',
  templateUrl: './review-wizard.component.html',
  styleUrls: ['./review-wizard.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    EvidenceAudioComponent,
    EvidenceImageComponent,
    RecordingPlayerComponent,
    DecimalPipe,
    NgForOf,
    NgIf,
  ]
})
export class EvaluationReviewWizardComponent implements OnInit, OnDestroy {

  season: EvaluationSeason;
  items: WorkItem[] = [];
  /** Broadcasts left out because an absolute condition already failed. */
  settledAtZero = 0;
  producers: string[] = [];
  producerFilter = '';
  index = 0;
  note = '';
  copiedPath = false;
  /** Cut in whole percent for a criterion an admin dials; see `applyCut`. */
  cut = 0;
  busy = false;

  private user: User | null = null;
  private all: EvaluationGame[] = [];
  private subscriptions = new Subscription();

  constructor(private evaluations: EvaluationsService, private auth: AuthService) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.evaluations.season(), this.evaluations.games()])
        .subscribe(([season, games]) => {
          this.season = season;
          this.all = games ?? [];
          this.rebuild();
        })
    );
    // The path on disk means nothing to a producer and is nobody's business
    // but the admin's, so the link waits for the user the same way the detail
    // screen does.
    this.subscriptions.add(this.auth.user$.subscribe(user => this.user = user));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private rebuild(): void {
    if (!this.season) {
      return;
    }
    // Keep the admin where they were: the list re-arrives from Firestore after
    // every ruling, and jumping back to the top each time would be unusable.
    const current = this.items[this.index]?.key;

    const items: WorkItem[] = [];
    let settled = 0;
    this.all
      .filter(game => game.state === 'toDecide' || game.state === 'returned')
      .filter(game => !this.producerFilter || game.producerName === this.producerFilter)
      .filter(game => {
        // A broadcast that already pays nothing — the recording stops in the
        // fourth inning, say — has a dozen open items whose answer changes
        // nothing. They are not work. Resolution no longer lands here:
        // a 720p stream costs a cut, so what else it did still matters.
        if (failedGates(game, this.season).length) {
          settled += 1;
          return false;
        }
        return true;
      })
      .forEach(game => {
        pendingCriteria(game, this.season).forEach(id => {
          const def = this.season.criteria.find(criterion => criterion.id === id);
          if (!def || def.role === 'declared') {
            // The operated camera is the producer's to declare; an admin only
            // confirms it later, so it is not work for this queue.
            return;
          }
          const measured = game.measurements?.[id];
          const matcher = EVIDENCE_FOR[id];
          items.push({
            key: `${game.videoId}:${id}`,
            game,
            def,
            note: measured?.note ?? '',
            at: measured?.at ?? null,
            evidence: matcher ? (game.events ?? []).filter(matcher) : [],
          });
        });
      });

    items.sort((a, b) => a.game.producerName.localeCompare(b.game.producerName)
      || a.game.date.localeCompare(b.game.date)
      || a.def.id.localeCompare(b.def.id));

    this.items = items;
    this.settledAtZero = settled;
    this.producers = [...new Set(this.all.map(game => game.producerName))].sort();
    const found = items.findIndex(item => item.key === current);
    this.index = found >= 0 ? found : Math.min(this.index, Math.max(items.length - 1, 0));
    this.reset();
  }

  get item(): WorkItem | null {
    return this.items[this.index] ?? null;
  }

  /** What this ruling is worth, so a close call can be weighed. */
  get swing(): number {
    const item = this.item;
    if (!item) {
      return 0;
    }
    const met = computeReward(item.game, this.season, {
      [item.def.id]: {criterion: item.def.id, value: 1, note: '', by: '', at: null, original: null},
    });
    const missed = computeReward(item.game, this.season, {
      [item.def.id]: {criterion: item.def.id, value: 0, note: '', by: '', at: null, original: null},
    });
    return met.total - missed.total;
  }

  get isAdmin(): boolean {
    return isEvaluationAdmin(this.user);
  }

  /** The sound sample, on the two criteria it actually settles. */
  get audioClip(): EvaluationAudioClip | null {
    if (!this.item || !AUDIO_FOR.includes(this.item.def.id)) {
      return null;
    }
    return (this.item.game.audioClips ?? []).find(clip => clip.kind === 'lineups') ?? null;
  }

  /** What the sample is, in words — where it starts and what it can settle. */
  get audioCaption(): string {
    const clip = this.audioClip;
    if (!clip) {
      return '';
    }
    const where = clip.movedPastSilence
      ? `Sound from ${this.time(clip.start)} — the lineup graphic itself was silent, `
        + 'so this is the first stretch with any sound'
      : `Sound from the lineup presentation, ${this.time(clip.start)}`;
    return this.item?.def.id === 'ambientSound'
      ? `${where} — anything audible here means the track is not dead`
      : `${where} — a commentator is talking here if the broadcast has one`;
  }

  /**
   * Chrome refuses to follow a file:// link from a page served over http, and
   * says nothing when it blocks one. Copying the path is the honest version of
   * the same thing: paste it into a player. The embedded player above covers
   * the usual case; this is for the reviewer who wants to scrub properly.
   */
  copyLocalFile(event: Event): void {
    const path = this.item?.game.localFile;
    if (!path) {
      return;
    }
    event.preventDefault();
    navigator.clipboard?.writeText(path);
    this.copiedPath = true;
    setTimeout(() => this.copiedPath = false, 2000);
  }

  time(seconds: number | null): string {
    if (seconds === null || seconds === undefined) {
      return '';
    }
    const whole = Math.floor(seconds);
    const minutes = Math.floor(whole / 60);
    const rest = whole % 60;
    return `${minutes}:${rest.toString().padStart(2, '0')}`;
  }

  facts(): { label: string; value: string }[] {
    const game = this.item?.game;
    if (!game) {
      return [];
    }
    const facts = game.facts ?? {};
    const rows: { label: string; value: string }[] = [];
    const push = (label: string, value: any) => {
      if (value !== null && value !== undefined && value !== '') {
        rows.push({label, value: String(value)});
      }
    };
    push('format', facts.format);
    if (facts.cameraPositions) {
      push('camera positions', `${facts.cameraPositions} (${(facts.cameraShares ?? [])
        .map(share => `${Math.round(share * 100)}%`).join(', ')})`);
    }
    if (facts.speechCoverage !== undefined && facts.speechCoverage !== null) {
      push('speech', `${Math.round(facts.speechCoverage * 100)}% of windows`);
    }
    if (facts.soundLevelDb !== undefined && facts.soundLevelDb !== null) {
      push('sound level', `${Math.round(facts.soundLevelDb)} dBFS`);
    }
    push('outages', facts.outageCount);
    if (facts.outageLongestS) {
      push('longest outage', `${Math.round(facts.outageLongestS)} s`);
    }
    push('highest inning', facts.highestInning);
    if (facts.finalHome !== undefined && facts.finalHome !== null) {
      push('score', `${facts.finalHome} : ${facts.finalAway}`);
    }
    return rows;
  }

  filterChanged(): void {
    this.index = 0;
    this.rebuild();
  }

  previous(): void {
    this.index = Math.max(0, this.index - 1);
    this.reset();
  }

  next(): void {
    this.index = Math.min(this.items.length - 1, this.index + 1);
    this.reset();
  }

  /**
   * The largest cut this criterion can take off the production fee, as whole
   * percent — or null when it is one of the criteria that only have two
   * answers. Where it is a number, "Not met" would be the far end of a range
   * rather than an answer, so the queue offers the range instead.
   */
  get maximumCutPercent(): number | null {
    const share = this.item ? maximumCut(this.item.def.id, this.season) : null;
    return share === null ? null : Math.round(share * 100);
  }

  /** Store a dialled cut as how much of the criterion was met; see reward.ts. */
  async applyCut(): Promise<void> {
    const maximum = this.maximumCutPercent;
    if (maximum === null) {
      return;
    }
    const cut = Math.max(0, Math.min(maximum, this.cut ?? 0));
    await this.decide(maximum === 0 ? 1 : 1 - cut / maximum);
  }

  private reset(): void {
    this.note = '';
    this.cut = 0;
    this.copiedPath = false;
  }

  async decide(value: number): Promise<void> {
    const item = this.item;
    if (!item) {
      return;
    }
    this.busy = true;
    try {
      await this.evaluations.decide(item.game.videoId, item.def.id, value, this.note,
        item.game.measurements?.[item.def.id]?.value ?? null);
      this.reset();
    } finally {
      this.busy = false;
    }
  }
}
